import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { askMoo } from '../../../queries/ask-moo';
import {
  createIntegration,
  updateIntegration,
  deleteIntegration,
  discoverIntegrationTools,
  updateIntegrationTool,
  validateCredentials,
  listGithubRepos,
} from '../api';
import type {
  ValidateCredentialsPayload,
  CreateIntegrationPayload,
  UpdateIntegrationPayload,
  ListGithubReposPayload,
} from '../types';
import { getData } from '../../../utils/functional/fp';

export function useIntegrationCatalog() {
  return useQuery({
    ...askMoo.integrationCatalog(),
    staleTime: 10 * 60 * 1000, // catalog changes rarely
  });
}

export function useIntegrationsList(params?: { status?: string }) {
  return useQuery(askMoo.integrations(params));
}

export function useIntegration(integrationId: string) {
  return useQuery({
    ...askMoo.integration(integrationId),
    enabled: !!integrationId,
  });
}

export function useIntegrationHealth(integrationId: string) {
  // Do not auto-fetch; trigger manually via refetch()
  return useQuery({
    ...askMoo.integrationHealth(integrationId),
    enabled: false,
  });
}

export function useValidateCredentials() {
  return useMutation({
    mutationFn: (data: ValidateCredentialsPayload) =>
      validateCredentials({ data }).then(getData),
  });
}

export function useListGithubRepos() {
  return useMutation({
    mutationFn: (data: ListGithubReposPayload) =>
      listGithubRepos({ data }).then(getData),
  });
}

export function useCreateIntegration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateIntegrationPayload) =>
      createIntegration({ data }).then(getData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: askMoo.integrations._def });
    },
  });
}

export function useUpdateIntegration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      integrationId,
      data,
    }: {
      integrationId: string;
      data: UpdateIntegrationPayload;
    }) => updateIntegration(integrationId, { data }).then(getData),
    onSuccess: (_result, { integrationId }) => {
      queryClient.invalidateQueries({ queryKey: askMoo.integrations._def });
      queryClient.invalidateQueries({
        queryKey: askMoo.integration(integrationId).queryKey,
      });
    },
  });
}

export function useDeleteIntegration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (integrationId: string) =>
      deleteIntegration(integrationId).then(getData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: askMoo.integrations._def });
    },
  });
}

export function useDiscoverTools() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (integrationId: string) =>
      discoverIntegrationTools(integrationId).then(getData),
    onSuccess: (_result, integrationId) => {
      queryClient.invalidateQueries({
        queryKey: askMoo.integration(integrationId).queryKey,
      });
    },
  });
}

export function useToggleTool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      integrationId,
      toolId,
      enabled,
    }: {
      integrationId: string;
      toolId: string;
      enabled: boolean;
    }) =>
      updateIntegrationTool(integrationId, toolId, { data: { enabled } }).then(
        getData
      ),
    onSuccess: (_result, { integrationId }) => {
      queryClient.invalidateQueries({
        queryKey: askMoo.integration(integrationId).queryKey,
      });
    },
  });
}
